import React, { useState, useRef, useEffect } from "react";
import radial from "../../../../../../assets/Group2.png";

const FutureIdentityCard = ({ 
  futureData = {},
  userName = "",
  profileImage = "",
  currentInput = "",
  coreValues = [],
  showCoreValues = false
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);

    const updateDuration = () => {
      if (isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      } else {
        const checkDuration = setInterval(() => {
          if (isFinite(audio.duration) && audio.duration > 0) {
            setDuration(audio.duration);
            clearInterval(checkDuration);
          }
        }, 200);
      }
    };

    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("ended", handleEnded);

    if (audio.readyState >= 1) updateDuration();

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (seconds) => {
    if (typeof seconds !== "number" || isNaN(seconds) || seconds < 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="bg-gradient-to-b from-[#707070] to-[#D9D9D9] p-[1px] rounded-[25px] h-full">
      <div className="bg-white rounded-[25px] overflow-hidden flex flex-col items-center h-full">
        
        {/* Header */}
        <div className="bg-[#54B0AF] w-full rounded-t-[25px] flex flex-col items-center justify-center py-6 relative">
          <p className="text-white text-xs font-gotham tracking-[0.15em] uppercase">FUTURE YOU</p>

          {/* Profile Image with #PROMOTED Banner */}
          <div className="relative mt-6">
            <div className="w-[164px] h-[164px] rounded-full bg-white border-[6px] border-[#F1F1F1] overflow-hidden flex items-center justify-center -mb-[82px] shadow-md relative">
              {profileImage ? (
                <img
                  src={profileImage}
                  alt="Future Profile"
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                <svg className="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              )}

              {/* #PROMOTED Overlay Banner */}
              <img
                src={radial}
                alt="Promoted"
                className="absolute top-0 left-0 z-20 pointer-events-none"
                style={{
                  width: '164px',
                  height: '164px',
                  objectFit: 'contain'
                }}
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="pt-[90px] pb-8 px-10 flex flex-col items-center text-center w-full">
          
          {/* Name */}
          <h2 className="text-center text-lg md:text-[23px] font-gotham font-semibold text-[#1C1C1C] mb-1">
            {userName || "User"}
          </h2>

          {/* Designation */}
          <p className="text-[#777777] text-[14px] font-gotham uppercase tracking-[0.5px] mb-6 w-full">
            {futureData?.designation || "DESIGNATION"}
          </p>

          {/* Info Section */}
          <div className="w-full border-y border-[#E3E3E3] py-4 text-[14px] font-gotham text-left space-y-3">
            <table className="w-full border-collapse">
              <tbody>
                {[
                  { label: "Salary", value: futureData?.salary },
                  { label: "Timeline", value: futureData?.timeline },
                  { label: "Location", value: futureData?.location },
                ].map((item, idx) => (
                  <tr key={idx}>
                    <td className="text-[#B3B3B3] text-[12px] uppercase tracking-[1px] font-medium w-[160px] align-top pr-1">
                      {item.label}
                    </td>
                    <td className="text-[#B3B3B3] w-[10px] text-center">:</td>
                    <td className="text-[#323232] text-[14px] font-normal pl-1">
                      {item.value ? (
                        item.value
                      ) : (
                        <div className="h-3 bg-gray-200/80 rounded-lg w-24 animate-pulse"></div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Known For */}
          <div className="mt-6 w-full text-left min-h-[120px]">
            <p className="text-center text-[12px] font-bold uppercase text-[#000] mb-2 tracking-[0.5px]">
              Known For
            </p>
            
            {(!futureData?.knownFor || futureData.knownFor.length === 0) && !currentInput?.trim() ? (
              <div className="space-y-3 px-2">
                <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-gray-300" /><div className="h-3 bg-gray-200/80 rounded-lg w-full animate-pulse" /></div>
                <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-gray-300" /><div className="h-3 bg-gray-200/80 rounded-lg w-full animate-pulse" /></div>
                <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-gray-300" /><div className="h-3 bg-gray-200/80 rounded-lg w-full animate-pulse" /></div>
                <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-gray-300" /><div className="h-3 bg-gray-200/80 rounded-lg w-full animate-pulse" /></div>
              </div>
            ) : (
              <ul className="text-[16px] text-[#323232] leading-[145%] font-gotham px-2 space-y-2">
                {futureData?.knownFor?.map((item, idx) => (
                  <li key={`item-${idx}`} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#323232]" />
                    <span>{item}</span>
                  </li>
                ))}

                {currentInput?.trim() &&
                  (futureData?.knownFor?.length || 0) < 4 && (
                    <li key="input" className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#323232]" />
                      <span>{currentInput}</span>
                    </li>
                  )}

                {Array.from({
                  length: Math.max(
                    0,
                    4 -
                      (futureData?.knownFor?.length || 0) -
                      (currentInput?.trim() ? 1 : 0)
                  ),
                }).map((_, index) => (
                  <li
                    key={`shimmer-${index}`}
                    className="flex items-center gap-2"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                    <div className="h-3 bg-gray-200/80 rounded-lg flex-1 animate-pulse" />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Core Values OR Audio Player */}
          {showCoreValues ? (
            coreValues && coreValues.length > 0 ? (
              <div className="mt-6 w-full">
                <div className="w-full border-t border-[#E3E3E3] py-4" />
                
                <p className="text-center text-[12px] font-bold uppercase text-[#000] mb-4 tracking-[0.5px]">
                  Your Core Values
                </p>
                
                <div className="flex flex-wrap justify-center gap-2 px-2">
                  {coreValues.map((value, index) => (
                    <div
                      key={index}
                      className="inline-flex items-center justify-center px-4 py-2 bg-[#54B0AF] rounded-full"
                    >
                      <span
                        style={{
                          fontFamily: "Gotham",
                          fontWeight: 400,
                          fontSize: "13px",
                          lineHeight: "1.2",
                          letterSpacing: "10%",
                          color: "#FFFFFF",
                          textAlign: "center",
                        }}
                      >
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-6 w-full">
                <div className="w-full border-t border-[#E3E3E3] py-4" />
                
                <p className="text-center text-[12px] font-bold uppercase text-[#000] mb-4 tracking-[0.5px]">
                  Your Core Values
                </p>
                
                <div className="flex flex-wrap justify-center gap-2 px-2">
                  {[0, 1, 2].map((index) => (
                    <div
                      key={index}
                      className="inline-flex items-center justify-center px-4 py-2 bg-gray-300 rounded-full"
                      style={{
                        minHeight: "auto",
                      }}
                    />
                  ))}
                </div>
              </div>
            )
          ) : (
            <div className="mt-4 w-full bg-[#D6EFEF] rounded-xl p-3">
              <p className="text-[10px] md:text-[13.13px] font-gotham text-[#54B0AF] uppercase tracking-wider font-bold text-center mb-2">
                Message from Future Self
              </p>
              
              {futureData?.futureVoiceNote ? (
                <>
                  <audio ref={audioRef} src={futureData.futureVoiceNote} />
                  <div className="flex items-center gap-2 w-full">
                    <button 
                      onClick={togglePlayPause}
                      className="w-5 h-5 flex-shrink-0 bg-[#333333] rounded-full flex items-center justify-center text-white hover:bg-[#444444] transition-colors"
                    >
                      {isPlaying ? (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                        </svg>
                      ) : (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      )}
                    </button>
                    <div className="flex-1 h-5 flex items-center gap-0.5 min-w-0">
                      {Array.from({ length: 36 }).map((_, i) => {
                        const progress = duration > 0 ? (currentTime / duration) * 40 : 0;
                        const isActive = i < progress;
                        return (
                          <div 
                            key={i} 
                            className={`flex-1 rounded-full transition-colors ${isActive ? 'bg-[#54B0AF]' : 'bg-[#BBD7D7]'}`}
                            style={{ height: `${Math.random() * 70 + 20}%` }}
                          />
                        );
                      })}
                    </div>
                    <span className="font-gotham text-[#54B0AF] font-medium text-xs flex-shrink-0 whitespace-nowrap">
                      {isPlaying ? formatTime(currentTime) : formatTime(duration)}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2 w-full">
                  <div className="w-5 h-5 flex-shrink-0 bg-[#333333] rounded-full flex items-center justify-center text-white">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                  <div className="flex-1 h-5 flex items-center gap-0.5 min-w-0">
                    {Array.from({ length: 36 }).map((_, i) => (
                      <div 
                        key={i} 
                        className="flex-1 bg-[#BBD7D7] rounded-full" 
                        style={{ height: `${Math.random() * 70 + 20}%` }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FutureIdentityCard;